import { useState, useCallback } from 'react';
import { ReactNode } from 'react';

/**
 * Create a validator function for required fields
 */
export function createRequiredValidator<T extends Record<string, any>>(requiredFields: Array<keyof T>) {
  return (values: T): Partial<Record<keyof T, string>> => {
    const errors = {} as Partial<Record<keyof T, string>>;

    requiredFields.forEach((field) => {
      const value = values[field];
      if (value === undefined || value === null || value === '') {
        errors[field] = 'Required';
      } else if (typeof value === 'number' && isNaN(value)) {
        errors[field] = 'Must be a valid number';
      }
    });

    return errors;
  };
}

/**
 * Hook for table field validation
 */
export function useTableValidation<T extends Record<string, any>, E extends Partial<Record<keyof T, string>> = Partial<Record<keyof T, string>>>(
  validator: (values: T) => E
) {
  const [validationErrors, setValidationErrors] = useState<E>({} as E);

  // Helper for generating text field props with validation
  const getTextFieldProps = useCallback(
    (field: keyof T, options?: { select?: boolean; required?: boolean; type?: string }) => ({
      required: options?.required !== false,
      select: !!options?.select,
      type: options?.type,
      error: !!validationErrors[field as keyof E],
      helperText: validationErrors[field as keyof E] as ReactNode,
      onFocus: () =>
        setValidationErrors({
          ...validationErrors,
          [field]: undefined,
        } as E),
    }),
    [validationErrors]
  );

  // Validate an item and update validation errors
  const validateItem = useCallback(
    (values: T): boolean => {
      const errors = validator(values);
      const hasErrors = Object.values(errors).some((error) => !!error);

      if (hasErrors) {
        setValidationErrors(errors);
        return false;
      }

      return true;
    },
    [validator]
  );

  // Clear all validation errors
  const clearValidationErrors = useCallback(() => {
    setValidationErrors({} as E);
  }, []);

  return {
    validationErrors,
    setValidationErrors,
    getTextFieldProps,
    validateItem,
    clearValidationErrors,
  };
}
